import { useMemo } from "react"

import { api, type RouterOutputs } from "~/utils/api"
import { sanitizeName } from "~/utils/helpers"

export type LocalUser = RouterOutputs["user"]["getOrgUsers"][number]

export const useGetUsers = (includeHakioUsers?: boolean) =>
  api.user.getOrgUsers.useQuery({ includeHakioUsers })

// Resolves user name/image by Auth0 ID or email. Memoized so the lookups stay stable for callers' deps.
export const useUserLookup = () => {
  const { data: users } = useGetUsers(true)

  return useMemo(() => {
    const findUser = (identifier: string) => {
      if (!identifier || !users) return undefined
      return users.find((u) => u.id === identifier) ?? users.find((u) => u.email === identifier)
    }

    return {
      getUserName: (identifier: string) => {
        const user = findUser(identifier)
        return sanitizeName(user?.name) || (users ? identifier : "")
      },
      getUserImage: (identifier: string) => findUser(identifier)?.picture ?? "",
      getUserEmail: (identifier: string) => findUser(identifier)?.email ?? "",
    }
  }, [users])
}
